import { useContext } from "react";
import { sortBy } from "lodash";
import { useHistory, useLocation, useParams } from "react-router";
import { RESOURCE_NAME } from "common/authorization";
import { OBJECT_TYPE, WORKFLOW_STATUS_MODE } from "common/constants";
import { buildLink, INFO_PANEL_TAB_NAME } from "common/routing";
import { getObjectDisplayForWorkflows } from "common-client/utils/customObject";
import { GetWorkflowQuery, useGetWorkflowQuery } from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn, FlexRow } from "../Common/__styles__/Layout";
import { ButtonLink } from "../Common/ButtonLink";
import { Button } from "../Common/Buttons/Button";
import FullPageFormLayout from "../Common/FullPageFormLayout";
import { Icon } from "../Common/Icons/LucideIcons";
import { ProgressBar } from "../Common/ProgressBar";
import { StyleVariant, Tag } from "../Common/Tag";
import { Body, Title } from "../Common/Typography";
import DropdownMenu from "../Inputs/DropdownMenu";
import { ApplicationInfo, WorkflowContainer } from "./__styles__/Workflow";
import { useDeleteWorkflowModal } from "./DeleteWorkflowModal";
import { useShareWorkflowModal } from "./ShareWorkflowModal";
import { WorkflowStep } from "./WorkflowStep";
import { getWorkflowProgress, getWorkflowStatus } from "./workflowUtils";

export const Workflow = () => {
  const { state } = useLocation<{ prevLocation?: string }>();
  const prevLocation = state?.prevLocation ?? buildLink("map");
  const { workflowId } = useParams<{
    workflowId: string;
  }>();

  const { data, loading } = useGetWorkflowQuery({
    variables: { workflowId },
    fetchPolicy: "network-only",
  });

  if (loading || !data?.workflow) {
    return null;
  }

  const workflowName = data.workflow.workflowDefinition.name;

  return (
    <FullPageFormLayout
      subtitle={workflowName}
      prevLocation={prevLocation}
      width="wide"
    >
      <WorkflowContent workflow={data.workflow} />
    </FullPageFormLayout>
  );
};

export const WorkflowContent = ({
  workflow,
}: {
  workflow: NonNullable<GetWorkflowQuery["workflow"]>;
}) => {
  const history = useHistory();
  const { isPublic, authorized, account } = useContext(AuthContext);

  const { state } = useLocation<{ prevLocation?: string }>();
  const prevLocation = state?.prevLocation ?? buildLink("map");

  const canShareWorkflow = authorized({
    permission: "share",
    resource: RESOURCE_NAME.WORKFLOW,
  });

  const canDeleteWorkflow = authorized({
    permission: "delete",
    resource: RESOURCE_NAME.WORKFLOW,
  });

  const [showShareWorkflowModal] = useShareWorkflowModal({
    workflow,
  });

  const [showDeleteWorkflowModal] = useDeleteWorkflowModal({
    workflow,
    onSuccess: () => history.push(prevLocation),
  });

  if (!workflow.property && !workflow.customMapGeometry) {
    return null;
  }

  const objectType =
    workflow.property ? OBJECT_TYPE.PROPERTY : OBJECT_TYPE.CUSTOM_MAP_GEOMETRY;
  const objectId = workflow.property?.id ?? workflow.customMapGeometry?.id;

  const objectDisplay =
    workflow.property ?
      getObjectDisplayForWorkflows({
        objectType: OBJECT_TYPE.PROPERTY,
        object: workflow.property,
        parcelFieldNameOverrides: account?.parcelFieldNameOverrides,
      })
    : workflow.customMapGeometry ?
      getObjectDisplayForWorkflows({
        objectType: OBJECT_TYPE.CUSTOM_MAP_GEOMETRY,
        object: workflow.customMapGeometry,
      })
    : null;

  const orderedWorkflowSteps = sortBy(workflow.workflowSteps, "order");

  const isManualMode =
    workflow.workflowDefinition.statusMode === WORKFLOW_STATUS_MODE.MANUAL;

  const { completed: completedCount, total: totalSteps } =
    getWorkflowProgress(workflow);

  let objectUrl: string;
  if (workflow.property) {
    objectUrl =
      isPublic ?
        buildLink("residentProperty", {
          propertyId: workflow.property.id,
        })
      : buildLink(
          "map",
          {},
          {
            propertyId: workflow.property.id,
            lat: workflow.property.latitude.toString(),
            lng: workflow.property.longitude.toString(),
            address: workflow.property.fullAddress,
            tab: INFO_PANEL_TAB_NAME.WORKFLOWS,
          },
        );
  } else if (workflow.customMapGeometry) {
    objectUrl =
      isPublic ?
        buildLink("residentCustomObject", {
          customMapId: workflow.customMapGeometry.customMapId,
          geometryId: workflow.customMapGeometry.id,
        })
      : buildLink(
          "map",
          {},
          {
            customMapId: workflow.customMapGeometry.customMapId,
            geometryId: workflow.customMapGeometry.id,
          },
        );
  }

  return (
    <WorkflowContainer isPublic={isPublic} data-testid="workflow-container">
      {isPublic && (
        <ButtonLink
          styleVariant="primary"
          size="medium"
          leftIconName="chevron-left"
          onClick={() =>
            history.push(
              buildLink("listWorkflows", {
                workflowDefinitionId: workflow.workflowDefinition.id,
              }),
            )
          }
          style={{ width: "fit-content" }}
        >
          {workflow.workflowDefinition.name}
        </ButtonLink>
      )}
      <ApplicationInfo>
        <FlexColumn gap="s">
          <FlexRow
            gap="s"
            style={{
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Body size="small" type="emphasis" color="contentSecondary">
              APPLICATION
            </Body>
            <FlexRow gap="s" style={{ alignItems: "center" }}>
              <Tag styleVariant={workflow.status.display as StyleVariant}>
                {getWorkflowStatus(
                  workflow.status.text,
                  workflow.workflowDefinition.statusMode,
                )}
              </Tag>
            </FlexRow>
          </FlexRow>
        </FlexColumn>

        <FlexColumn gap="s">
          {objectDisplay && (
            <Title size="default" type="semiBold">
              {objectDisplay}
            </Title>
          )}
        </FlexColumn>
        <FlexRow gap="s" style={{ alignItems: "center" }}>
          <Button
            variant="secondary"
            size="small"
            onPress={() => history.push(objectUrl)}
            title={
              objectType === OBJECT_TYPE.PROPERTY ?
                "View property"
              : "View object"
            }
          />
          {!isPublic && (canShareWorkflow || canDeleteWorkflow) && (
            <DropdownMenu
              actions={[
                ...(canShareWorkflow ?
                  [
                    {
                      label: "Share",
                      onClick: showShareWorkflowModal,
                    },
                  ]
                : []),
                ...(canDeleteWorkflow ?
                  [
                    {
                      label: "Delete",
                      variant: "red" as const,
                      onClick: showDeleteWorkflowModal,
                    },
                  ]
                : []),
              ]}
              customButton={({ onPress }) => (
                <Icon
                  iconName="ellipsis"
                  color="contentSecondary"
                  size={20}
                  onClick={onPress}
                  style={{ cursor: "pointer" }}
                />
              )}
            />
          )}
        </FlexRow>
      </ApplicationInfo>
      <ProgressBar value={completedCount} max={totalSteps} />
      <FlexColumn gap="m">
        {orderedWorkflowSteps.map(step => (
          <WorkflowStep
            key={step.id}
            workflowStep={step}
            isManualMode={isManualMode}
            objectId={objectId}
            objectType={objectType}
            workflow={workflow}
          />
        ))}
      </FlexColumn>
    </WorkflowContainer>
  );
};
